import React, { useEffect } from "react";
import { Photo } from "./MasonryGrid";
import styles from "./Lightbox.module.css";

interface LightboxProps {
  photo: Photo | null;
  onClose: () => void;
  onNext?: () => void;
  onPrev?: () => void;
}

export const Lightbox: React.FC<LightboxProps> = ({
  photo,
  onClose,
  onNext,
  onPrev,
}) => {
  useEffect(() => {
    // Only run if there's a photo (lightbox is open)
    if (!photo) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && onNext) onNext();
      if (e.key === "ArrowLeft" && onPrev) onPrev();
    };

    document.addEventListener("keydown", handleKeyDown);

    // Prevent body scroll when lightbox is open
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.top = `-${window.scrollY}px`;

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      // Restore body scroll
      const scrollY = document.body.style.top;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0", 10) * -1);
      }
    };
  }, [photo, onClose, onNext, onPrev]);

  if (!photo) return null;

  return (
    <div className={styles.lightbox} onClick={onClose}>
      <div
        className={styles.lightboxContent}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close lightbox"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {onPrev && (
          <button
            className={`${styles.navButton} ${styles.prevButton}`}
            onClick={onPrev}
            aria-label="Previous photo"
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        <img src={photo.src} alt={photo.alt} className={styles.lightboxImage} />

        {onNext && (
          <button
            className={`${styles.navButton} ${styles.nextButton}`}
            onClick={onNext}
            aria-label="Next photo"
          >
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 18L15 12L9 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}

        <div className={styles.lightboxInfo}>
          <p>{photo.projectName}</p>
        </div>
      </div>
    </div>
  );
};
